import { useEffect, useState } from "react";
import type { AgentRun, ChatMessage } from "../../api/agent";
import styles from "./ChatInterface.module.css";
export function activityLabel(message?: ChatMessage) {
  const tool = message?.metadata?.tool_name;
  if (!tool) return "Working on your request";
  const labels: Record<string, string> = {
    web_fetch: "Reading a web page",
    web_search: "Searching the web",
    search_web: "Searching the web",
    get_website_profile: "Reading the workspace profile",
    create_briefing: "Preparing a content brief",
  };
  return labels[tool] || "Using a tool";
}
export default function AgentProgress({
  run,
  messages,
  connected,
  onStop,
  canStop,
  isThinking,
}: {
  run?: AgentRun | null;
  messages: ChatMessage[];
  connected?: boolean;
  onStop?: () => void;
  canStop?: boolean;
  isThinking: boolean;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const active =
    isThinking || run?.status === "running" || run?.status === "stopping";
  if (!active) {
    return run?.status === "stopped" ? (
      <p className={styles.outcome} role="status">
        Further agent work stopped. Operations already started may still finish.
      </p>
    ) : run?.status === "failed" ? (
      <p role="status">This run failed. {run.message}</p>
    ) : null;
  }
  // A terminal record may still be visible while the next request awaits admission.
  const currentRun =
    run && ["running", "stopping", "awaiting_approval"].includes(run.status)
      ? run
      : null;
  const requestIndex = messages.reduce(
    (last, message, index) => (message.type === "user" ? index : last),
    -1,
  );
  const request = requestIndex >= 0 ? messages[requestIndex] : undefined;
  const activity = currentRun
    ? messages.filter(
        (message) => message.metadata?.run_id === currentRun.run_id,
      )
    : messages.slice(requestIndex + 1);
  const event = activity[activity.length - 1];
  const start = currentRun
    ? Date.parse(currentRun.started_at)
    : request?.timestamp.getTime() || now;
  const elapsed = Math.max(0, Math.floor((now - start) / 1000));
  const last = Math.max(
    event?.timestamp.getTime() || 0,
    currentRun ? Date.parse(currentRun.updated_at) : start,
  );
  const idle = Math.max(0, Math.floor((now - (last || now)) / 1000));
  return (
    <div className={styles.progress} role="status">
      <div>
        <strong>
          {run?.status === "stopping"
            ? "Stopping…"
            : connected === false
              ? "Connection lost; work may still be running"
              : idle >= 30
                ? "Still working — no recent update"
                : activityLabel(
                    [...activity].reverse().find((m) => m.type === "TOOL_CALL"),
                  )}
        </strong>
        <p>
          {elapsed}s elapsed · Last update {idle}s ago
        </p>
      </div>
      {canStop && onStop && (
        <button type="button" onClick={onStop}>
          Stop
        </button>
      )}
    </div>
  );
}
